import { Alert, Box } from '@mui/material';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { useDevice } from '@/api/devices';
import { BaroPressureItemFormattedWithUnits, useHistoricalBarometricPressureData } from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import pressureIcon from '@/assets/BarometerIcon.svg';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { colorsets } from '@/charts/common';
import { addRenderersByProcessingSource } from '@/charts/helpers';
import { AlertLine } from '@/charts/types';
import { SensorIcon } from '@/components/data/SensorIcon';
import {
    ChartDataDetails,
    ChartWithInfo,
    ProcessingSourceLegendItems,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { useGlobalStore } from '@/store';
import { composeAlertLines } from '@/utils';

type LineConfig = Line<BaroPressureItemFormattedWithUnits>;

export const BAROMETRIC_PRESSURE_LINE = {
    id: 'barometric-pressure',
    label: 'Air pressure',
    valueKey: 'value' as const,
    color: colorsets.blue,
    unitKey: 'units' as const,
    unitTypeKey: 'unitType' as const,
} satisfies LineConfig;

const LINES = [BAROMETRIC_PRESSURE_LINE];

export type BarometricPressureChartProps = {
    chartTitle: string;
    spotterId: string;
    queryEnabled?: boolean;
    showAlertLines: boolean;
    scaleToFitAlerts: boolean;
    alertLines?: AlertLine[];
    lines: LineConfig[];
    onSelectData?: (data: LineDataSlice<BaroPressureItemFormattedWithUnits> | null) => void;
    onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
};

export const BarometricPressureChart = ({
    chartTitle,
    spotterId,
    queryEnabled = true,
    showAlertLines,
    scaleToFitAlerts,
    alertLines,
    onSelectData,
    onSelectTimestamp,
    lines,
}: BarometricPressureChartProps) => {
    const id = useId();
    const { dateRange } = useDateRangeContext();
    const chartRef = useRef<LineChart<BaroPressureItemFormattedWithUnits> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const { selectedTimestamp } = useTimelineNavigationContext();
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;
    const timeZone = usePreferredTimeZone();

    const containerRef = useRef<HTMLElement>(null);
    const containerId = `barometric-pressure-chart${id}`;
    const { width, height } = useElementSize(containerRef);

    useTrackChartView({
        containerEl,
        spotterId,
        chartTitle,
    });

    const { data: barometricPressure, isPending } = useHistoricalBarometricPressureData({
        spotterId: spotterId!,
        dateRange,
        processingSources: 'all',
        config: { enabled: queryEnabled },
    });

    const drawChart = useCallback(drawLineChart, []);

    const linesWithRenderers = useMemo(
        () => addRenderersByProcessingSource(lines, barometricPressure),
        [lines, barometricPressure]
    );

    useEffect(() => {
        if (isPending) {
            return;
        }

        if (!barometricPressure) {
            return;
        }

        const newChart = drawChart<BaroPressureItemFormattedWithUnits>({
            dataset: barometricPressure,
            lines: linesWithRenderers,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Barometric pressure',
            containerId,
            showAlertLines,
            scaleChartToFitAllAlerts: scaleToFitAlerts,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        alertLines,
        barometricPressure,
        containerId,
        dateRange,
        timeZone,
        drawChart,
        height,
        isPending,
        linesWithRenderers,
        onSelectData,
        onSelectTimestamp,
        scaleToFitAlerts,
        showAlertLines,
        width,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [barometricPressure?.length, dateRange]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export const BarometricPressureSection = () => {
    const { setSelectedTimestamp } = useTimelineNavigationContext();
    const chartTitle = 'Barometric pressure';
    const { data: device } = useDevice();
    const { dateRange } = useDateRangeContext();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);
    const [selectedData, setSelectedData] = useState<LineDataSlice<BaroPressureItemFormattedWithUnits> | null>(null);
    const spotterId = device?.spotterId;

    const {
        data: barometricPressure,
        isPending,
        isFetching,
    } = useHistoricalBarometricPressureData({
        spotterId: spotterId!,
        dateRange,
        processingSources: 'all',
    });
    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertLinesOn },
    });

    const alertLines = useMemo(
        () => composeAlertLines({ deviceAlerts, triggerTypes: ['spotter_threshold_barometricpressure'] }),
        [deviceAlerts]
    );

    if (!device?.externalHardware.barometer && !device?.airPressure) {
        return;
    }

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title="Barometric pressure"
            icon={<SensorIcon src={pressureIcon} />}
        >
            {barometricPressure?.length ? (
                <ChartWithInfo
                    legend={<ProcessingSourceLegendItems data={barometricPressure} />}
                    chart={
                        <BarometricPressureChart
                            onSelectTimestamp={setSelectedTimestamp}
                            lines={LINES}
                            spotterId={spotterId!}
                            chartTitle={chartTitle}
                            onSelectData={setSelectedData}
                            showAlertLines={isAlertLinesOn}
                            scaleToFitAlerts={isScaleToFitAlertLinesOn}
                            alertLines={alertLines}
                        />
                    }
                >
                    <ChartDataDetails>
                        {!selectedData && (
                            <LineChartDataSummary
                                dateRange={dateRange}
                                lines={LINES}
                                dataset={barometricPressure}
                                renderLineSummary={() => (
                                    <>
                                        <DataLineMean />
                                        <DataLineExtent />
                                    </>
                                )}
                            />
                        )}
                        <DataDetailsByTimestamp
                            selectedData={selectedData}
                            datumRenderer={({ lines, datum }) => (
                                <LineChartDatumDetail dataset={barometricPressure} datum={datum} lines={lines} />
                            )}
                        />
                    </ChartDataDetails>
                </ChartWithInfo>
            ) : (
                <Alert severity="info">No barometric pressure data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
